import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, BadgeCheck } from 'lucide-react';
const TESTIMONIALS = [{
  name: 'Margaret W.',
  location: 'Phoenix, AZ',
  rating: 5,
  date: 'Jan 28, 2025',
  title: 'I can walk to the mailbox without thinking twice',
  text: "I was terrified of stairs. After two weeks with this method, I realized I hadn't braced once going down my front steps. That's when I knew something had changed. The traffic light system is genius—it took the guesswork out completely.",
  verified: true
}, {
  name: 'Robert K.',
  location: 'Columbus, OH',
  rating: 5,
  date: 'Feb 3, 2025',
  title: 'Finally, a program that doesn\'t say "push through it"',
  text: 'I\'ve tried three different PT programs and quit all of them because they flared me up. This one actually respects sensitive days. Day 7 I noticed my knee felt "quieter." By day 12, I walked the grocery store without my cane for the first time in months.',
  verified: true
}, {
  name: 'Linda S.',
  location: 'Tampa, FL',
  rating: 5,
  date: 'Feb 10, 2025',
  title: 'My husband noticed before I did',
  text: 'He said "you\'re not holding the railing anymore." I hadn\'t even realized it. The exercises are so simple—chair, wall, band—but the sequence makes all the difference. I actually look forward to my 20 minutes now.',
  verified: true
}, {
  name: 'James T.',
  location: 'Denver, CO',
  rating: 4,
  date: 'Jan 15, 2025',
  title: 'Skeptical at first, but the results speak',
  text: "I'm 67 and my knee has been \"bad\" for years. I didn't expect much from an ebook. But the angle reset exercises on Day 1 made my morning stiffness noticeably better by Day 3. Not perfect—but predictable. And that's what I needed.",
  verified: true
}, {
  name: 'Patricia M.',
  location: 'Portland, OR',
  rating: 5,
  date: 'Feb 18, 2025',
  title: 'The flare-up protocol saved my progress',
  text: 'Day 9 I overdid it gardening and woke up swollen. Old me would have quit everything. But the flare-up protocol told me exactly what to do. Two days later I was back on track. That alone is worth the price.',
  verified: true
}, {
  name: 'David R.',
  location: 'Austin, TX',
  rating: 5,
  date: 'Mar 1, 2025',
  title: 'Walked my daughter down the aisle',
  text: "I bought this because I was afraid my knee would give out at my daughter's wedding. Did the full 15 days before the big day. Walked her down the aisle without a single wobble. I'm not crying, you're crying.",
  verified: true
}];
function StarRating({
  rating
}: {
  rating: number;
}) {
  return <div className="flex gap-[2px]" data-id="element-328">
      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={16} className={i <= rating ? 'text-[#00B67A] fill-[#00B67A]' : 'text-gray-300 fill-gray-300'} data-id="element-329" />)}
    </div>;
}
export function TestimonialCarousel() {
  const [currentPage, setCurrentPage] = useState(0);
  const reviewsPerPage = 2;
  const totalPages = Math.ceil(TESTIMONIALS.length / reviewsPerPage);
  const currentReviews = TESTIMONIALS.slice(currentPage * reviewsPerPage, currentPage * reviewsPerPage + reviewsPerPage);
  const next = () => setCurrentPage(prev => (prev + 1) % totalPages);
  const prev = () => setCurrentPage(prev => (prev - 1 + totalPages) % totalPages);
  const avgRating = (TESTIMONIALS.reduce((sum, t) => sum + t.rating, 0) / TESTIMONIALS.length).toFixed(1);
  return <section className="py-[40px] px-[15px] bg-white" data-id="element-330">
      <div className="container max-w-[700px] mx-auto px-[15px]" data-id="element-331">
        {/* Trustpilot-style header */}
        <div className="text-center mb-[30px]" data-id="element-332">
          <h2 className="text-[24px] font-[700] text-[#111827] font-['Montserrat'] mb-[12px]" data-id="element-333">
            Trusted by Real People
          </h2>
          <div className="flex items-center justify-center gap-3 mb-2" data-id="element-334">
            <span className="text-[28px] font-[800] text-[#111827]" data-id="element-335">
              {avgRating}
            </span>
            <div className="flex flex-col items-start" data-id="element-336">
              <div className="flex gap-[2px]" data-id="element-337">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} size={20} className="text-[#00B67A] fill-[#00B67A]" data-id="element-338" />)}
              </div>
              <span className="text-[12px] text-[#6B7280]" data-id="element-339">
                Based on 126 reviews
              </span>
            </div>
          </div>
          <div className="flex items-center justify-center gap-1 text-[13px] text-[#6B7280]" data-id="element-340">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="inline-block" data-id="element-341">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#00B67A" data-id="element-342" />
            </svg>
            <span className="font-[600] text-[#00B67A]" data-id="element-343">Excellent</span>
            <span className="text-gray-400 mx-1" data-id="element-344">|</span>
            <span data-id="element-345">Verified purchases</span>
          </div>
        </div>

        {/* Review cards */}
        <div className="flex flex-col gap-[16px] min-h-[320px]" data-id="element-346">
          {currentReviews.map((testimonial, index) => <div key={currentPage * reviewsPerPage + index} className="bg-[#F8FAFB] border border-gray-200 rounded-[10px] p-[20px] shadow-sm" data-id="element-347">
              <div className="flex items-center justify-between mb-[10px]" data-id="element-348">
                <StarRating rating={testimonial.rating} data-id="element-349" />
                <span className="text-[11px] text-[#9CA3AF]" data-id="element-350">
                  {testimonial.date}
                </span>
              </div>

              <h4 className="text-[15px] font-[700] text-[#111827] mb-[6px] font-['Montserrat']" data-id="element-351">
                {testimonial.title}
              </h4>

              <p className="text-[14px] text-[#4B5563] leading-[1.6] mb-[12px]" data-id="element-352">
                "{testimonial.text}"
              </p>

              <div className="flex items-center justify-between border-t border-gray-100 pt-[10px]" data-id="element-353">
                <div className="flex items-center gap-[8px]" data-id="element-354">
                  <div className="w-[32px] h-[32px] rounded-full bg-[#DC2626] flex items-center justify-center text-white font-[700] text-[13px]" data-id="element-355">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div data-id="element-356">
                    <div className="text-[13px] font-[600] text-[#111827]" data-id="element-357">
                      {testimonial.name}
                    </div>
                    <div className="text-[11px] text-[#9CA3AF]" data-id="element-358">
                      {testimonial.location}
                    </div>
                  </div>
                </div>
                {testimonial.verified && <div className="flex items-center gap-1 text-[11px] text-[#00B67A] font-[600]" data-id="element-359">
                    <BadgeCheck size={14} data-id="element-360" />
                    Verified
                  </div>}
              </div>
            </div>)}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-[16px] mt-[20px]" data-id="element-361">
          <button onClick={prev} className="w-[36px] h-[36px] rounded-full border border-gray-200 flex items-center justify-center text-[#6B7280] hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer" data-id="element-362">
            <ChevronLeft size={18} data-id="element-363" />
          </button>
          <div className="flex gap-[6px]" data-id="element-364">
            {Array.from({
            length: totalPages
          }).map((_, i) => <button key={i} onClick={() => setCurrentPage(i)} className={`w-[8px] h-[8px] rounded-full transition-all cursor-pointer border-none ${i === currentPage ? 'bg-[#DC2626] w-[20px]' : 'bg-gray-300'}`} data-id="element-365" />)}
          </div>
          <button onClick={next} className="w-[36px] h-[36px] rounded-full border border-gray-200 flex items-center justify-center text-[#6B7280] hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer" data-id="element-366">
            <ChevronRight size={18} data-id="element-367" />
          </button>
        </div>
      </div>
    </section>;
}